import { useState } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { LockKeyhole, Check } from 'lucide-react';
import {
  netsuiteSigningKeyQueryOptions,
  uploadNetsuiteSigningKey,
} from '../../lib/queries/customers';

function fmtFingerprint(fp: string | null): string {
  if (!fp) return '—';
  return (fp.slice(0, 16).match(/.{1,2}/g)?.join(':') ?? fp) + '…';
}

export default function NetSuiteSigningKey({ id }: { id: string }) {
  const qc = useQueryClient();
  const { data } = useQuery(netsuiteSigningKeyQueryOptions(id));
  const [pem, setPem] = useState('');
  const [editing, setEditing] = useState(false);

  const mutation = useMutation({
    mutationFn: () => uploadNetsuiteSigningKey(id, pem),
    onSuccess: () => {
      setPem(''); // don't linger in memory
      setEditing(false);
      qc.invalidateQueries({ queryKey: ['netsuite-signing-key', id] });
    },
  });

  const provisioned = data?.provisioned;

  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) setPem(await file.text());
  }

  return (
    <div className="bg-bg-elevated border-primary/8 rounded-lg border p-5">
      <div className="flex items-center justify-between">
        <span className="text-subtle text-xs font-semibold tracking-widest uppercase">
          NetSuite Signing Key
        </span>
        {provisioned && !editing && (
          <button
            onClick={() => setEditing(true)}
            className="text-primary/70 hover:text-primary border-primary/10 rounded border px-3 py-1 text-[0.625rem] transition-colors"
          >
            Replace
          </button>
        )}
      </div>

      {provisioned && !editing ? (
        <div className="border-primary/8 mt-3 flex items-center gap-3 rounded border p-4">
          <div className="bg-success flex h-5.5 w-5.5 shrink-0 items-center justify-center rounded-full">
            <Check size={10} className="text-primary" />
          </div>
          <div className="text-xs">
            <p className="text-primary font-medium">
              Provisioned
              {data?.modulusBits ? ` · RSA ${data.modulusBits}` : ''}
            </p>
            <p className="text-subtle mt-0.5 font-mono">
              SHA-256 {fmtFingerprint(data?.fingerprint ?? null)}
            </p>
          </div>
        </div>
      ) : (
        <div className="mt-3 space-y-3">
          <p className="text-muted text-xs">
            Paste the tenant's PKCS8 RSA private key (PEM) used to sign the
            client-credentials JWT assertion. It is encrypted at rest and never
            shown again after upload.
          </p>
          <textarea
            value={pem}
            onChange={(e) => setPem(e.target.value)}
            autoComplete="off"
            spellCheck={false}
            placeholder="-----BEGIN PRIVATE KEY-----"
            className="text-primary placeholder:text-primary/20 h-36 w-full rounded border border-white/10 bg-black/40 px-3 py-2 font-mono text-xs focus:border-[--color-accent] focus:outline-none"
          />
          <div className="flex items-center justify-between gap-2">
            <label className="text-muted hover:text-primary flex cursor-pointer items-center gap-1.5 text-xs">
              <LockKeyhole size={12} />
              Upload .pem file
              <input
                type="file"
                accept=".pem,.key,.txt"
                onChange={onFile}
                className="hidden"
              />
            </label>
            <div className="flex gap-2">
              {editing && (
                <button
                  onClick={() => {
                    setEditing(false);
                    setPem('');
                  }}
                  className="text-muted hover:text-primary rounded border border-white/10 px-3 py-1 text-xs"
                >
                  Cancel
                </button>
              )}
              <button
                onClick={() => mutation.mutate()}
                disabled={!pem.trim() || mutation.isPending}
                className="bg-accent rounded px-3 py-1 text-xs text-white disabled:opacity-40"
              >
                {mutation.isPending ? 'Saving…' : 'Save key'}
              </button>
            </div>
          </div>
          {mutation.isError && (
            <p className="text-danger text-xs">
              {(mutation.error as Error).message}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
